import { createSlice } from '@reduxjs/toolkit';
import { transcribeWhatsAppMessage } from '../whatsAppMessage/whatsAppMessageThunks';
import {
  deleteWhatsAppSession,
  fetchWhatsAppSessions,
} from './whatsAppSessionThunks';
import type { WhatsAppSessionState } from './whatsAppSessionTypes';

const initialState: WhatsAppSessionState = {
  byPersonId: {},
  status: 'idle',
  error: null,
};

export const whatsAppSessionSlice = createSlice({
  name: 'whatsAppSessions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Il ricarico periodico del polling passa `silent: true` e NON muove
      // status/error: sono lo stato del caricamento CHIESTO dall'utente. I dati
      // invece si aggiornano sempre — è tutto il punto del polling.
      .addCase(fetchWhatsAppSessions.pending, (state, action) => {
        if (action.meta.arg.silent) return;
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchWhatsAppSessions.fulfilled, (state, action) => {
        if (!action.meta.arg.silent) state.status = 'succeeded';
        state.byPersonId[action.payload.personId] = action.payload.sessions;
      })
      .addCase(fetchWhatsAppSessions.rejected, (state, action) => {
        if (action.meta.arg.silent) return;
        state.status = 'failed';
        state.error = action.error.message ?? 'Errore nel caricamento sessioni';
      })
      // Delete: rimuove la sessione da ogni lista per-contatto (non sappiamo a
      // priori quale personId la contenga, la sessione è unica per id).
      .addCase(deleteWhatsAppSession.fulfilled, (state, action) => {
        const removedId = action.payload;
        for (const key of Object.keys(state.byPersonId)) {
          const personId = Number(key);
          state.byPersonId[personId] = state.byPersonId[personId].filter(
            (session) => session.whatsAppSessionId !== removedId,
          );
        }
      })
      // Trascrizione: il messaggio aggiornato arriva da un thunk di whatsAppMessage,
      // ma i messaggi non hanno uno slice proprio — vivono annidati qui. Lo si
      // sostituisce in loco, così il testo resta anche richiudendo l'accordion.
      //
      // La scansione è su tutti i contatti perché il messaggio porta solo il proprio
      // id: né personId né whatsAppSessionId. Sono manciate di elementi, non vale la
      // pena di un indice.
      .addCase(transcribeWhatsAppMessage.fulfilled, (state, action) => {
        const updated = action.payload;
        for (const key of Object.keys(state.byPersonId)) {
          const personId = Number(key);
          state.byPersonId[personId] = state.byPersonId[personId].map(
            (session) => {
              if (
                !session.messages.some(
                  (m) => m.whatsAppMessageId === updated.whatsAppMessageId,
                )
              ) {
                return session;
              }
              return {
                ...session,
                messages: session.messages.map((m) =>
                  m.whatsAppMessageId === updated.whatsAppMessageId
                    ? updated
                    : m,
                ),
              };
            },
          );
        }
      });
  },
});

export const whatsAppSessionReducer = whatsAppSessionSlice.reducer;
